import { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Link } from "react-router-dom";
import api from "../../../api";
import AvatarPreview from "../../../components/AvatarPreview"

export default function Avatar() {
  const [avatars, setAvatars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  useEffect(() => {
    const loadAvatars = async () => {
      try {
        const res = await api.fetchAvatars();
        setAvatars(res.data);
      } catch (err) {
        setError("Failed to load avatars:", err);
      } finally {
        setLoading(false);
      }
    };
    loadAvatars();
  }, []);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold">Avatars</h1>
        <Link to="/avatar-new" className="text-blue-600 w-full sm:w-auto">
          <button className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            + New Avatar
          </button>
        </Link>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white shadow rounded-lg overflow-x-auto">
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-gray-600">Loading avatars...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">Error: {error}</p>
            </div>
          ) : avatars.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No avatar found.</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-gray-600 border-b">
                  <th className="py-3 px-4 text-sm font-semibold">ID</th>
                  <th className="py-3 px-4 text-sm font-semibold">Name</th>
                  <th className="py-3 px-4 text-sm font-semibold">Age Group</th>
                  <th className="py-3 px-4 text-sm font-semibold">Avatar ID</th>
                  <th className="py-3 px-4 text-sm font-semibold">Provider</th>
                  <th className="py-3 px-4 text-sm font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {avatars.map((a) => (
                  <tr key={a.id} className="border-b hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">{a.id}</td>
                    <td className="py-3 px-4">{a.name}</td>
                    <td className="py-3 px-4">{a.age_group}</td>
                    <td
                      className="py-3 px-4 relative"
                      onMouseEnter={() => setHoveredId(a.avatar_id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      {a.avatar_id}
                      {hoveredId === a.avatar_id && (
                        <div className="absolute left-0 top-full mt-2 w-full h-[300px] border rounded-lg z-10 bg-white shadow-lg">
                          <AvatarPreview
                            avatarUrl={a.avatar_id}
                          />
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">{a.avatar_provider}</td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${a.is_active ? 'text-green-600' : 'text-red-600'}`}>
                        {a.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {loading ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-600">Loading avatars...</p>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-red-500">Error: {error}</p>
          </div>
        ) : avatars.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-600">No avatar found.</p>
          </div>
        ) : (
          avatars.map((a) => (
            <div
              key={a.id}
              className="bg-white shadow rounded-lg p-4 border border-gray-200"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-xs text-gray-500 font-medium">ID</span>
                  <span className="text-sm font-semibold text-gray-900">{a.id}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Name</span>
                  <span className="text-sm text-gray-900 font-medium">{a.name}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Age Group</span>
                  <span className="text-sm text-gray-900">{a.age_group}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Avatar ID</span>
                  <span className="text-sm text-gray-900 break-all">{a.avatar_id}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Provider</span>
                  <span className="text-sm text-gray-900">{a.avatar_provider}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500 font-medium">Status</span>
                  <span className={`text-sm font-medium ${a.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {a.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                
                {/* Avatar Preview for Mobile */}
                <div className="pt-2 border-t">
                  <div className="w-full h-[200px] border rounded-lg bg-gray-50">
                    <AvatarPreview
                      avatarUrl={a.avatar_id}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
